import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchCloudMaterials, fetchTemporaryMaterials } from '../../../features/materials/api';
import { useGenerationAssets } from '../../../components/generation/state/assetsContext';
import { useToast } from '../../../hooks/useToast';

interface Params {
  /** 未保存画像を個人フォルダへ保存するときの保存関数 (商品種別ごとに引数が異なる) */
  saveForSelection: (imageUrl: string) => Promise<any>;
}

/**
 * 素材一覧 API / 保存 API の has_product_links (無ければ ASIN の有無) から、選択素材の商品紐づきフラグを決める。
 * 保存 API は商品リンクを実際に作れたかを has_product_links で返す (リクエストに案件IDを渡しただけでは紐づき扱いにしない)
 */
function hasProductLinksOf(material: { asin?: string | string[] | null }): boolean {
  const flag = (material as { has_product_links?: boolean }).has_product_links;
  if (typeof flag === 'boolean') return flag;
  return Array.isArray(material.asin) ? material.asin.length > 0 : !!material.asin;
}

/**
 * 商品詳細ページ (Amazon / マニュアル) 共通の「画像クリックで選択済み素材に
 * 追加/解除」フロー。
 *
 * 組織スコープの素材一覧 (cloud/temporary) を取得し、選択状態の判定と
 * トグル (未保存画像は個人フォルダへ保存してから追加) を提供する。
 * 未保存画像は保存の完了を待たずに「選択中」を出し (楽観的表示)、保存に失敗したら外して知らせる。
 */
export function useProductImageSelection({ saveForSelection }: Params) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { addAsset, removeAsset, selectedAssets } = useGenerationAssets();
  // 個人フォルダへ保存中の画像 URL。保存が終わるまでの間も選択中として表示する
  const [savingUrls, setSavingUrls] = useState<ReadonlySet<string>>(() => new Set());
  const setSaving = (imageUrl: string, saving: boolean) => {
    setSavingUrls((prev) => {
      const next = new Set(prev);
      if (saving) next.add(imageUrl);
      else next.delete(imageUrl);
      return next;
    });
  };

  const { data: cloudMaterialsData } = useQuery({
    queryKey: ['cloud-materials-all'],
    queryFn: () => fetchCloudMaterials({ limit: 1000 }),
    staleTime: 5 * 60 * 1000, // 5分間はキャッシュを新鮮とみなす
    refetchOnWindowFocus: false, // ウィンドウフォーカス時の自動再フェッチを無効化
  });

  const { data: temporaryMaterialsData } = useQuery({
    queryKey: ['temporary-materials-all'],
    queryFn: () => fetchTemporaryMaterials({ limit: 1000 }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const cloudMaterials = cloudMaterialsData?.items ?? [];
  const temporaryMaterials = temporaryMaterialsData?.items ?? [];

  const isImageSelected = (imageUrl: string) => {
    if (savingUrls.has(imageUrl)) return true;
    // 選択済み素材のsourceUrlまたはthumbnailUrlと一致するかチェック
    // また、cloudMaterials/temporaryMaterialsのIDで選択されている場合もチェック
    const cloudMaterial = cloudMaterials.find((m) =>
      m.source_url === imageUrl || m.preview_url === imageUrl || m.previewUrl === imageUrl
    );
    const tempMaterial = temporaryMaterials.find((m) =>
      m.source_url === imageUrl || m.preview_url === imageUrl || m.previewUrl === imageUrl
    );
    return selectedAssets.some((asset) =>
      asset.sourceUrl === imageUrl ||
      asset.thumbnailUrl === imageUrl ||
      (cloudMaterial && asset.id === cloudMaterial.id) ||
      (tempMaterial && asset.id === tempMaterial.id)
    );
  };

  // 画像をクリックして選択済み素材を追加/削除（トグル）
  const handleAddToSelection = async (imageUrl: string) => {
    // 保存中の再クリックは無視する (保存の完了を待って追加するため、ここで解除しても戻ってくる)
    if (savingUrls.has(imageUrl)) return;
    // 既に保存済みの素材を探す
    const cloudMaterial = cloudMaterials.find((m) =>
      m.source_url === imageUrl || m.preview_url === imageUrl || m.previewUrl === imageUrl
    );
    const tempMaterial = temporaryMaterials.find((m) =>
      m.source_url === imageUrl || m.preview_url === imageUrl || m.previewUrl === imageUrl
    );

    // 既に選択中の場合は解除
    const selectedAsset = selectedAssets.find((asset) =>
      asset.sourceUrl === imageUrl ||
      asset.thumbnailUrl === imageUrl ||
      (cloudMaterial && asset.id === cloudMaterial.id) ||
      (tempMaterial && asset.id === tempMaterial.id)
    );
    if (selectedAsset) {
      removeAsset(selectedAsset.id);
      toast.success('選択を解除しました');
      return;
    }

    if (cloudMaterial) {
      // 共有フォルダに保存済み
      addAsset({
        id: cloudMaterial.id,
        thumbnailUrl: cloudMaterial.thumbnail_url || cloudMaterial.thumbnailUrl || imageUrl,
        type: 'cloud',
        sourceUrl: cloudMaterial.source_url || cloudMaterial.sourceUrl,
        storageBucket: cloudMaterial.storage_bucket,
        storagePath: cloudMaterial.storage_path,
        asin: cloudMaterial.asin,
        hasProductLinks: hasProductLinksOf(cloudMaterial),
        width: cloudMaterial.width,
        height: cloudMaterial.height,
      });
      toast.success('選択済み素材に追加しました');
    } else if (tempMaterial) {
      // 個人フォルダに保存済み
      addAsset({
        id: tempMaterial.id,
        thumbnailUrl: tempMaterial.thumbnail_url || tempMaterial.thumbnailUrl || imageUrl,
        type: 'temporary',
        sourceUrl: tempMaterial.source_url || tempMaterial.sourceUrl,
        storageBucket: tempMaterial.storage_bucket,
        storagePath: tempMaterial.storage_path,
        asin: tempMaterial.asin,
        hasProductLinks: hasProductLinksOf(tempMaterial),
        width: tempMaterial.width,
        height: tempMaterial.height,
      });
      toast.success('選択済み素材に追加しました');
    } else {
      // 未保存の場合は個人フォルダに保存してから追加。保存中も選択中として見せる
      setSaving(imageUrl, true);
      try {
        const result = await saveForSelection(imageUrl);
        // このフックが読む一覧 (temporary-materials-all) も無効化する。していなかったため、保存済みでも一覧に見えず
        // クリックのたびに同じ画像を個人フォルダへ保存し直していた
        queryClient.invalidateQueries({ queryKey: ['temporary-materials'] });
        queryClient.invalidateQueries({ queryKey: ['temporary-materials-all'] });
        addAsset({
          id: result.id,
          thumbnailUrl: result.thumbnail_url || result.thumbnailUrl || imageUrl,
          type: 'temporary',
          sourceUrl: result.source_url || result.sourceUrl,
          storageBucket: result.storage_bucket,
          storagePath: result.storage_path,
          asin: result.asin,
          hasProductLinks: hasProductLinksOf(result),
          width: result.width,
          height: result.height,
        });
        toast.success('保存して選択済み素材に追加しました');
      } catch {
        toast.error('素材の保存に失敗したため、選択を取り消しました');
      } finally {
        setSaving(imageUrl, false);
      }
    }
  };

  return { cloudMaterials, temporaryMaterials, isImageSelected, handleAddToSelection };
}
